/**
 * Queue Simulation - Selectors
 *
 * All derived reading of a run happens here rather than in components: which
 * run is on screen, how it compares to today, and whether a difference is big
 * enough to mean anything.
 */

import { createSelector } from '@ufc/core'
import type { SimulationState } from './simulation.slice'
import type { SimRun } from '../domain/types'
import { compareRuns, isMaterialDifference } from '../domain/utils/simulation.utils'

interface RootState {
  queueSimulation: SimulationState
}

const selectState = (state: RootState) => state.queueSimulation

export const selectSimSeed = createSelector(selectState, (s) => s.seed)
export const selectSimSeedLoading = createSelector(selectState, (s) => s.seedLoading)
export const selectSimSeedError = createSelector(selectState, (s) => s.seedError)
export const selectSimCommands = createSelector(selectState, (s) => s.commands)
export const selectSimCurrentMin = createSelector(selectState, (s) => s.currentMin)
export const selectSimRuns = createSelector(selectState, (s) => s.runs)
export const selectSimRunning = createSelector(selectState, (s) => s.running)
export const selectSimRunError = createSelector(selectState, (s) => s.runError)
export const selectSimCompare = createSelector(selectState, (s) => s.compare)
export const selectSimOrders = createSelector(selectState, (s) => s.orders)
export const selectSimOrdersLoading = createSelector(selectState, (s) => s.ordersLoading)
export const selectSimOrderSearch = createSelector(selectState, (s) => s.orderSearch)
export const selectSimMode = createSelector(selectState, (s) => s.mode)
export const selectSimDate = createSelector(selectState, (s) => s.date)
export const selectSimDays = createSelector(selectState, (s) => s.days)
export const selectSimDaysLoading = createSelector(selectState, (s) => s.daysLoading)
export const selectSimPlaying = createSelector(selectState, (s) => s.playing)

export const selectSimProducts = createSelector(selectSimSeed, (seed) => seed?.products ?? [])

export const selectSimFactory = createSelector(selectSimSeed, (seed) => seed?.factory ?? null)

export const selectSimCaveats = createSelector(selectSimSeed, (seed) => seed?.caveats ?? [])

/** The run whose yard is drawn — the first, which is today's rules. */
export const selectPrimaryRun = createSelector(
  selectSimRuns,
  (runs): SimRun | null => runs[0] ?? null,
)

/** Whether a bay has been closed by hand in this simulation. */
export const selectClosedBayIds = createSelector(selectPrimaryRun, (run) =>
  new Set((run?.snapshot.bays ?? []).filter((b) => b.manuallyClosed).map((b) => b.id)),
)

/**
 * The other runs measured against the first.
 *
 * Empty when only one set of rules is being run — there is nothing to compare
 * against, and inventing a baseline would be worse than showing none.
 */
export const selectRunComparison = createSelector(selectSimRuns, (runs) =>
  runs.length < 2 ? [] : compareRuns(runs),
)

/** True when at least one compared run differs enough to act on. */
export const selectHasMaterialDifference = createSelector(selectRunComparison, (rows) =>
  rows.some((row) => isMaterialDifference(row)),
)
